/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * PasswordResetRequestPage Component
 * ============================================================================
 *
 * Password reset request page with email input and rate limiting.
 *
 * ============================================================================
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { HelpCircle, Mail, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormInput } from '@/components/ui/FormInput';
import { Button } from '@/components/ui/Button';
import { useRequestPasswordReset } from '@/hooks/api/useAuth';
import { getErrorMessage, isApiError } from '@/services/api/errors';

// Validation schema factory
const createRequestSchema = (t: (key: string) => string) => z.object({
  email: z.string()
    .min(1, t('validation.required'))
    .email(t('validation.email')),
});

type RequestFormData = z.infer<ReturnType<typeof createRequestSchema>>;

export function PasswordResetRequestPage() {
  const { t } = useTranslation('auth');
  const requestResetMutation = useRequestPasswordReset();

  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestCount, setRequestCount] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);

  // Memoized schema with translations
  const requestSchema = useMemo(() => createRequestSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      email: '',
    },
  });

  const emailValue = watch('email');

  const onSubmit = async (data: RequestFormData) => {
    setError(null);

    // Check if CAPTCHA is needed (after 1st request) - client-side only
    if (requestCount >= 1 && !showCaptcha) {
      setShowCaptcha(true);
      return;
    }

    requestResetMutation.mutate(
      { email: data.email },
      {
        onSuccess: () => {
          setRequestCount((prev) => prev + 1);
          setIsSuccess(true);
        },
        onError: (err) => {
          if (isApiError(err)) {
            if (err.code === 'RATE_LIMITED') {
              setError(t('notifications.networkError'));
            } else {
              setError(err.message || getErrorMessage(err.code));
            }
          } else {
            setError(t('notifications.error'));
          }
        },
      }
    );
  };

  // Success state
  if (isSuccess) {
    return (
      <AuthLayout
        title={t('auth.passwordReset.checkEmail.title')}
        subtitle={t('auth.passwordReset.checkEmail.subtitle')}
        icon={<CheckCircle size={28} />}
      >
        <div className="reset-success">
          <div className="reset-success-icon">
            <Mail size={48} />
          </div>

          <p className="reset-success-message">
            {t('auth.passwordReset.checkEmail.message').replace('{{email}}', emailValue)}
          </p>

          <div className="reset-success-note">
            <p>{t('auth.passwordReset.checkEmail.notReceived')}</p>
            <ul>
              <li>{t('auth.passwordReset.checkEmail.checkSpam')}</li>
              <li>{t('auth.passwordReset.checkEmail.correctEmail')}</li>
              <li>{t('auth.passwordReset.checkEmail.waitMinutes')}</li>
            </ul>
          </div>

          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              setIsSuccess(false);
              setShowCaptcha(false);
            }}
          >
            {t('auth.passwordReset.checkEmail.sendAnother')}
          </Button>

          <Link to="/auth/login" className="reset-back-link">
            <ArrowLeft size={16} />
            {t('auth.mfa.backToLogin')}
          </Link>
        </div>

        <style>{`
          .reset-success {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            text-align: center;
          }

          .reset-success-icon {
            width: 80px;
            height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--color-success-light);
            color: var(--color-success);
            border-radius: 50%;
            animation: pulse 2s ease-in-out infinite;
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }

          .reset-success-message {
            font-size: var(--text-md);
            color: var(--color-text-secondary);
            line-height: 1.6;
            margin: 0;
          }

          .reset-success-message strong {
            color: var(--color-text-primary);
          }

          .reset-success-note {
            width: 100%;
            padding: 16px;
            background: var(--color-bg-tertiary);
            border-radius: var(--radius-lg);
            text-align: left;
          }

          .reset-success-note p {
            font-size: var(--text-sm);
            font-weight: 500;
            color: var(--color-text-primary);
            margin: 0 0 8px 0;
          }

          .reset-success-note ul {
            margin: 0;
            padding-left: 20px;
          }

          .reset-success-note li {
            font-size: var(--text-sm);
            color: var(--color-text-secondary);
            margin-bottom: 4px;
          }

          .reset-back-link {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: var(--text-sm);
            color: var(--color-text-muted);
            text-decoration: none;
            transition: color 0.2s ease;
          }

          .reset-back-link:hover {
            color: var(--color-text-primary);
          }

          @media (max-width: 640px) {
            .reset-success {
              gap: 1.25rem;
            }
            .reset-success-icon {
              width: 64px;
              height: 64px;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .reset-success-icon { animation: none !important; }
          }
        `}</style>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.passwordReset.requestTitle')}
      subtitle={t('auth.passwordReset.requestSubtitle')}
      icon={<HelpCircle size={28} />}
      iconVariant="gold"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="reset-form">
        {/* Error message */}
        {error && (
          <div className="reset-error">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Email field */}
        <FormInput
          label={t('auth.passwordReset.email')}
          type="email"
          autoComplete="email"
          placeholder={t('auth.passwordReset.emailPlaceholder')}
          leftIcon={<Mail size={18} />}
          error={errors.email?.message}
          {...register('email')}
          value={emailValue}
        />

        {/* Mock CAPTCHA (replace with real implementation) */}
        {showCaptcha && (
          <div className="reset-captcha">
            <p className="captcha-label">{t('auth.passwordReset.captcha.label')}</p>
            <div className="captcha-placeholder">
              {/* Replace with actual CAPTCHA implementation */}
              <label className="captcha-checkbox">
                <input type="checkbox" required />
                <span className="checkbox-mark" />
                <span>{t('auth.passwordReset.captcha.notRobot')}</span>
              </label>
            </div>
          </div>
        )}

        {/* Submit button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={requestResetMutation.isPending}
          loadingText={t('auth.passwordReset.sending')}
          glow
        >
          {t('auth.passwordReset.sendLink')}
        </Button>

        {/* Back to login */}
        <div className="reset-footer">
          <Link to="/auth/login" className="reset-back-link">
            <ArrowLeft size={16} />
            {t('auth.mfa.backToLogin')}
          </Link>
        </div>
      </form>

      <style>{`
        .reset-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .reset-error {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--color-error-light);
          border: 1px solid var(--color-error);
          border-radius: var(--radius-lg);
          color: var(--color-error);
          font-size: var(--text-sm);
        }

        .reset-captcha {
          padding: 16px;
          background: var(--color-bg-tertiary);
          border-radius: var(--radius-lg);
        }

        .captcha-label {
          font-size: var(--text-sm);
          color: var(--color-text-secondary);
          margin: 0 0 12px 0;
        }

        .captcha-placeholder {
          padding: 12px;
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-md);
        }

        .captcha-checkbox {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
        }

        .captcha-checkbox input {
          position: absolute;
          opacity: 0;
        }

        .captcha-checkbox .checkbox-mark {
          width: 20px;
          height: 20px;
          border: 2px solid var(--color-border-default);
          border-radius: 4px;
          background: var(--color-bg-primary);
          position: relative;
          transition: all 0.2s ease;
        }

        .captcha-checkbox input:checked + .checkbox-mark {
          background: var(--color-success);
          border-color: var(--color-success);
        }

        .captcha-checkbox .checkbox-mark::after {
          content: '';
          position: absolute;
          display: none;
          left: 6px;
          top: 2px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        .captcha-checkbox input:checked + .checkbox-mark::after {
          display: block;
        }

        .captcha-checkbox span:last-child {
          font-size: var(--text-sm);
          color: var(--color-text-primary);
        }

        .reset-footer {
          display: flex;
          justify-content: center;
          padding-top: 8px;
        }

        .reset-back-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: var(--text-sm);
          color: var(--color-text-muted);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .reset-back-link:hover {
          color: var(--color-text-primary);
        }

        @media (max-width: 640px) {
          .reset-form {
            gap: 1rem;
          }
          .reset-footer {
            padding-top: 4px;
          }
        }
      `}</style>
    </AuthLayout>
  );
}

export default PasswordResetRequestPage;
